/**
 * Ảnh nhúng trong `.chilldoc` — APM-CDIMG-a, docs/plan-chilldoc-images.md §2.
 *
 * Mỗi ảnh là một chú thích HTML **sau** khối `chilldoc-data`, một dòng base64:
 *
 * ```
 * <!-- chilldoc-image i3f2a…(32 hex) image/webp
 * UklGR…
 * -->
 * ```
 *
 * Nằm sau cùng để markdown đầu tệp (LLM, job trích chữ) và JSON của `doc` không
 * bị ảnh đẩy xuống. Base64 không có `-` nên không chuỗi nào đóng chú thích sớm.
 * Node ảnh trỏ tới khối bằng thuộc tính `imageId`; `src` trong tệp là
 * `chilldoc-image:{id}`, không bao giờ `data:` (plan §2.2).
 */
/** Trần một ảnh **sau khi nén** — lớn hơn thì nên là tệp chèn, không phải ảnh nhúng (plan §1.2). */
export const CHILLDOC_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
/** `src` của ảnh nhúng trong tệp lưu. */
export const CHILLDOC_IMAGE_SRC = 'chilldoc-image:';
/** `i` + 32 hex: băm 128-bit của nội dung đã nén — cùng ảnh ⇒ cùng id ⇒ một khối. */
export const CHILLDOC_IMAGE_ID = /^i[0-9a-f]{32}$/;
/** Định dạng `<img>` nào cũng vẽ được — khớp `EDITOR_IMAGE_TYPES` của web. */
export const CHILLDOC_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const IMAGE_OPEN = '<!-- chilldoc-image ';
const IMAGE_CLOSE = '\n-->';
const BASE64 = /^[A-Za-z0-9+/]*={0,2}$/;
export function isChilldocImageId(value) {
    return typeof value === 'string' && CHILLDOC_IMAGE_ID.test(value);
}
/**
 * Mọi `imageId` hợp lệ ở **bất kỳ đâu** trong giá trị (vỏ, `doc`, thuộc tính
 * lồng nhau). Đi sâu thay vì chỉ theo `content` để ảnh nằm trong thuộc tính —
 * ô ảnh của slide, vùng Mở đầu / Kết thúc ở vỏ (`-d`) — không bị bỏ sót lúc ghi.
 */
export function chilldocImageIds(value) {
    const ids = new Set();
    const walk = (current) => {
        if (Array.isArray(current)) {
            for (const item of current)
                walk(item);
            return;
        }
        if (!current || typeof current !== 'object')
            return;
        for (const [key, child] of Object.entries(current)) {
            if (key === 'imageId' && isChilldocImageId(child))
                ids.add(child);
            else
                walk(child);
        }
    };
    walk(value);
    return [...ids];
}
/** Chỉ những ảnh `value` còn tham chiếu — xoá ảnh khỏi tài liệu rồi lưu là khối ảnh đi theo. */
export function usedImages(value, images) {
    const kept = {};
    for (const id of chilldocImageIds(value)) {
        const image = images[id];
        if (image && isValidImage(image))
            kept[id] = image;
    }
    return kept;
}
function isValidImage(image) {
    return (CHILLDOC_IMAGE_TYPES.includes(image.mime) &&
        image.data.length > 0 &&
        image.data.length % 4 === 0 &&
        BASE64.test(image.data));
}
/** Dòng của khối ảnh để nối vào cuối tệp (mỗi khối kết bằng một dòng trống). */
export function imageBlocks(images) {
    return Object.entries(images).flatMap(([id, image]) => [
        `${IMAGE_OPEN}${id} ${image.mime}`,
        image.data,
        '-->',
        '',
    ]);
}
/**
 * Đọc khối ảnh từ vị trí `from` — **không ném**. Id sai dạng, MIME lạ, base64
 * hỏng, khối không đóng ⇒ bỏ khối đó; ảnh ấy hiện thành ô trống, cả tệp vẫn mở.
 */
export function readImageBlocks(text, from = 0) {
    const images = {};
    let at = text.indexOf(IMAGE_OPEN, from);
    while (at >= 0) {
        const headerEnd = text.indexOf('\n', at);
        if (headerEnd < 0)
            break;
        const close = text.indexOf(IMAGE_CLOSE, headerEnd);
        if (close < 0)
            break;
        const [id, mime] = text
            .slice(at + IMAGE_OPEN.length, headerEnd)
            .trim()
            .split(/\s+/);
        const image = { mime: mime ?? '', data: text.slice(headerEnd + 1, close).trim() };
        if (isChilldocImageId(id) && isValidImage(image))
            images[id] = image;
        at = text.indexOf(IMAGE_OPEN, close + IMAGE_CLOSE.length);
    }
    return images;
}
//# sourceMappingURL=chilldoc-images.js.map